import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import { REPORT } from "@/constants/testIds";
import {
  ArrowLeft, Download, CheckCircle, AlertTriangle, AlertCircle,
  FileText, User, Calendar, Building2, Loader, Share2, Copy, Check, X,
  Link2, Link2Off, Pencil, Save, Stethoscope, Send, Mail
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Doctor Notes ────────────────────────────────────────────────────────────

function DoctorNotesSection({ reportId, initialNotes }) {
  const [notes,   setNotes]   = useState(initialNotes || "");
  const [draft,   setDraft]   = useState(initialNotes || "");
  const [editing, setEditing] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const textareaRef           = useRef();

  const startEdit = () => { setDraft(notes); setEditing(true); };
  const cancel    = () => { setDraft(notes); setEditing(false); };

  useEffect(() => {
    if (editing) textareaRef.current?.focus();
  }, [editing]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/reports/${reportId}/notes`, { notes: draft }, { withCredentials: true });
      setNotes(draft);
      setEditing(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      // Notes save failed silently — user can retry
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid={REPORT.notesSection} className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Stethoscope size={17} className="text-amber-700 flex-shrink-0" />
          <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
            Doctor's Notes
          </h2>
          <span className="text-xs font-medium text-amber-700 bg-amber-100 border border-amber-200 px-2 py-0.5 rounded-full">
            Included when shared
          </span>
        </div>
        <div className="flex items-center gap-2">
          {saved && (
            <span className="flex items-center gap-1 text-emerald-600 text-xs font-medium">
              <Check size={12} /> Saved
            </span>
          )}
          {!editing && (
            <button
              data-testid={REPORT.notesEditButton}
              onClick={startEdit}
              className="flex items-center gap-1.5 text-xs font-medium text-amber-700 hover:text-amber-900 bg-white border border-amber-200 px-3 py-1.5 rounded-lg hover:bg-amber-50 transition-colors">
              <Pencil size={12} /> {notes ? "Edit" : "Add Notes"}
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      {editing ? (
        <div>
          <textarea
            ref={textareaRef}
            data-testid={REPORT.notesTextarea}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={5}
            maxLength={2000}
            placeholder="Add clinical observations, follow-up recommendations, or notes for the patient…"
            className="w-full px-3.5 py-3 border border-amber-300 rounded-lg text-sm text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent resize-none placeholder-amber-300 transition-all leading-relaxed"
          />
          <div className="flex items-center justify-between mt-3">
            <span className="text-xs text-amber-500">{draft.length}/2000</span>
            <div className="flex gap-2">
              <button
                onClick={cancel}
                className="px-3 py-1.5 text-sm text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors">
                Cancel
              </button>
              <button
                data-testid={REPORT.notesSaveButton}
                onClick={save}
                disabled={saving}
                className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-60">
                {saving ? <><Loader size={13} className="animate-spin" /> Saving…</> : <><Save size={13} /> Save notes</>}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <p
          data-testid={REPORT.notesDisplay}
          className={`text-sm leading-relaxed whitespace-pre-wrap ${notes ? "text-slate-700" : "text-amber-400 italic"}`}
          onClick={startEdit}
          style={{ cursor: notes ? "default" : "pointer" }}>
          {notes || "No notes yet — click Add Notes to include clinical observations visible to the patient when this report is shared."}
        </p>
      )}
    </div>
  );
}

function ShareModal({ reportId, initialShared, initialToken, onClose }) {
  const frontendUrl = window.location.origin;
  const [isShared, setIsShared]     = useState(initialShared || false);
  const [shareUrl, setShareUrl]     = useState(
    initialShared && initialToken ? `${frontendUrl}/shared/${initialToken}` : ""
  );
  const [toggling, setToggling]     = useState(false);
  const [copied,   setCopied]       = useState(false);
  const [sendEmail, setSendEmail]   = useState("");
  const [sending,   setSending]     = useState(false);
  const [sendStatus, setSendStatus] = useState(null); // null | "success" | "error"
  const [sendMsg,   setSendMsg]     = useState("");
  const inputRef                    = useRef();

  const toggle = async () => {
    setToggling(true);
    try {
      if (isShared) {
        await axios.delete(`${API}/reports/${reportId}/share`, { withCredentials: true });
        setIsShared(false);
        setShareUrl("");
        setSendStatus(null);
      } else {
        const { data } = await axios.post(`${API}/reports/${reportId}/share`, {}, { withCredentials: true });
        setIsShared(true);
        setShareUrl(data.share_url);
      }
    } catch (e) {
      // Share toggle failed silently — toggle UI will stay in previous state
    } finally {
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleSendNotification = async () => {
    if (!sendEmail.trim()) return;
    setSending(true);
    setSendStatus(null);
    try {
      const { data } = await axios.post(
        `${API}/reports/${reportId}/share/notify`,
        { to_email: sendEmail.trim() },
        { withCredentials: true }
      );
      setSendStatus("success");
      setSendMsg(data.message || `Email sent to ${sendEmail}`);
      setSendEmail("");
    } catch (e) {
      setSendStatus("error");
      const detail = e.response?.data?.detail;
      setSendMsg(typeof detail === "string" ? detail : "Failed to send. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div data-testid={REPORT.shareModal} className="relative bg-white border border-slate-200 rounded-xl shadow-xl w-full max-w-md p-6">
        <button onClick={onClose}
          className="absolute top-4 right-4 p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors">
          <X size={16} />
        </button>

        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
            <Share2 size={18} className="text-blue-900" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Share Report</h3>
            <p className="text-xs text-slate-400">Generate a read-only link for patients or colleagues</p>
          </div>
        </div>

        {/* Toggle row */}
        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg mb-4">
          <div className="flex items-center gap-2.5">
            {isShared ? <Link2 size={16} className="text-blue-900" /> : <Link2Off size={16} className="text-slate-400" />}
            <div>
              <p className="text-sm font-medium text-slate-800">
                {isShared ? "Sharing enabled" : "Sharing disabled"}
              </p>
              <p className="text-xs text-slate-400">
                {isShared ? "Anyone with the link can view" : "Only you can view this report"}
              </p>
            </div>
          </div>
          <button
            data-testid={REPORT.shareToggle}
            onClick={toggle}
            disabled={toggling}
            className={`relative w-11 h-6 rounded-full transition-colors duration-200 flex-shrink-0 ${
              isShared ? "bg-blue-900" : "bg-slate-300"
            } disabled:opacity-50`}>
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
              isShared ? "translate-x-5" : "translate-x-0"
            }`} />
            {toggling && <Loader size={10} className="absolute inset-0 m-auto text-white animate-spin" />}
          </button>
        </div>

        {/* Link row */}
        {isShared && shareUrl && (
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  data-testid={REPORT.shareLinkInput}
                  readOnly
                  value={shareUrl}
                  onClick={() => inputRef.current?.select()}
                  className="flex-1 px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-lg text-slate-600 font-mono cursor-text focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  data-testid={REPORT.shareCopyButton}
                  onClick={copyLink}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all flex-shrink-0 ${
                    copied ? "bg-emerald-600 text-white" : "bg-blue-900 text-white hover:bg-blue-800"
                  }`}>
                  {copied ? <><Check size={14} /> Copied!</> : <><Copy size={14} /> Copy</>}
                </button>
              </div>
              <p className="text-xs text-slate-400 flex items-center gap-1.5">
                <AlertCircle size={11} className="flex-shrink-0" />
                Toggle off above to revoke access at any time
              </p>
            </div>

            {/* Send to patient */}
            <div className="border-t border-slate-100 pt-4">
              <p className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
                <Mail size={12} className="text-slate-500" /> Send directly to patient
              </p>
              <div className="flex gap-2">
                <input
                  data-testid={REPORT.shareEmailInput}
                  type="email"
                  value={sendEmail}
                  onChange={(e) => { setSendEmail(e.target.value); setSendStatus(null); }}
                  placeholder="patient@example.com"
                  onKeyDown={(e) => e.key === "Enter" && handleSendNotification()}
                  className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                />
                <button
                  data-testid={REPORT.shareSendButton}
                  onClick={handleSendNotification}
                  disabled={sending || !sendEmail.trim()}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-blue-900 text-white hover:bg-blue-800 transition-colors disabled:opacity-50 flex-shrink-0">
                  {sending
                    ? <><Loader size={13} className="animate-spin" /> Sending…</>
                    : <><Send size={13} /> Send</>}
                </button>
              </div>
              {sendStatus === "success" && (
                <p data-testid={REPORT.shareSendSuccess} className="flex items-center gap-1.5 text-xs text-emerald-600 mt-2">
                  <Check size={12} /> {sendMsg}
                </p>
              )}
              {sendStatus === "error" && (
                <p data-testid={REPORT.shareSendError} className="flex items-center gap-1.5 text-xs text-red-600 mt-2">
                  <AlertCircle size={12} /> {sendMsg}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function OverallBadge({ status }) {
  const config = {
    normal: { bg: "bg-emerald-50 border-emerald-200", text: "text-emerald-700", icon: CheckCircle, label: "All Normal" },
    requires_attention: { bg: "bg-amber-50 border-amber-200", text: "text-amber-700", icon: AlertTriangle, label: "Needs Attention" },
    critical: { bg: "bg-red-50 border-red-200", text: "text-red-700", icon: AlertCircle, label: "Critical" },
  };
  const c = config[status] || config.normal;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold border ${c.bg} ${c.text}`}>
      <c.icon size={14} /> {c.label}
    </span>
  );
}

// Strip verbose parenthetical Lipoprotein descriptions
// "HDL (High-Density Lipoprotein) Cholesterol" → "HDL Cholesterol"
function cleanMarkerName(name = "") {
  return name.replace(/\s*\([^)]*[Ll]ipoprotein[^)]*\)/g, "").trim();
}

function MarkerRow({ marker, idx }) {
  const statusMap = {
    normal: { row: "hover:bg-slate-50", badge: "badge-normal" },
    abnormal: { row: "bg-red-50 hover:bg-red-100", badge: "badge-abnormal" },
    borderline: { row: "bg-yellow-50 hover:bg-yellow-100", badge: "badge-borderline" },
  };
  const s = statusMap[marker.status] || statusMap.normal;

  return (
    <tr data-testid={`${REPORT.markerRow}-${idx}`} className={`border-b border-slate-100 transition-colors ${s.row}`}>
      <td className="py-3 px-4 text-sm font-medium text-slate-900">{cleanMarkerName(marker.name)}</td>
      <td className="py-3 px-4 text-sm font-mono text-slate-800 font-semibold">{marker.value}</td>
      <td className="py-3 px-4 text-sm text-slate-500">{marker.unit}</td>
      <td className="py-3 px-4 text-sm text-slate-500">{marker.reference_range}</td>
      <td className="py-3 px-4">
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${s.badge}`}>
          {marker.status.charAt(0).toUpperCase() + marker.status.slice(1)}
        </span>
      </td>
      <td className="py-3 px-4 text-xs text-slate-500 max-w-xs">{marker.explanation}</td>
    </tr>
  );
}

export default function ReportPage() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  const [showShare, setShowShare] = useState(false);

  useEffect(() => {
    axios.get(`${API}/reports/${reportId}`, { withCredentials: true })
      .then(({ data }) => setReport(data))
      .catch(() => setError("Report not found"))
      .finally(() => setLoading(false));
  }, [reportId]); // API and axios are module-level constants; state setters are stable

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const resp = await axios.get(`${API}/reports/${reportId}/download`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      const pname = report?.patient_info?.name?.replace(/\s+/g, "_") || "patient";
      link.setAttribute("download", `blood_report_${pname}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert("Download failed. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" }); }
    catch { return iso; }
  };

  const abnormalCount = report ? (report.blood_markers || []).filter((m) => m.status === "abnormal").length : 0;
  const borderlineCount = report ? (report.blood_markers || []).filter((m) => m.status === "borderline").length : 0;

  if (loading) return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="flex items-center justify-center py-24">
        <div className="w-8 h-8 border-4 border-blue-900 border-t-transparent rounded-full animate-spin" />
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <AlertCircle size={48} className="text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-medium text-slate-700">{error}</h2>
        <button onClick={() => navigate("/dashboard")} className="mt-4 text-blue-900 hover:underline text-sm">
          Back to Dashboard
        </button>
      </div>
    </div>
  );

  const pi = report?.patient_info || {};

  return (
    <div className="min-h-screen bg-slate-50" data-testid={REPORT.page}>
      <Navbar />

      {showShare && (
        <ShareModal
          reportId={reportId}
          initialShared={report?.is_shared}
          initialToken={report?.share_token}
          onClose={() => setShowShare(false)}
        />
      )}

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <button
              data-testid={REPORT.backButton}
              onClick={() => navigate("/dashboard")}
              className="p-2 text-slate-400 hover:text-slate-700 hover:bg-white border border-transparent hover:border-slate-200 rounded-lg transition-all">
              <ArrowLeft size={18} />
            </button>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
                {pi.name || "Blood Test Report"}
              </h1>
              <p className="text-slate-400 text-sm">{report?.original_filename}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <OverallBadge status={report?.overall_status} />
            <button
              data-testid={REPORT.shareButton}
              onClick={() => setShowShare(true)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium border transition-colors ${
                report?.is_shared
                  ? "bg-blue-50 text-blue-900 border-blue-200 hover:bg-blue-100"
                  : "bg-white text-slate-600 border-slate-300 hover:border-blue-300"
              }`}>
              <Share2 size={15} />
              {report?.is_shared ? "Shared" : "Share"}
            </button>
            <button
              data-testid={REPORT.downloadButton}
              onClick={handleDownload}
              disabled={downloading}
              className="flex items-center gap-2 bg-blue-900 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-blue-800 transition-colors disabled:opacity-60">
              {downloading ? <Loader size={15} className="animate-spin" /> : <Download size={15} />}
              {downloading ? "Preparing..." : "Download PDF"}
            </button>
          </div>
        </div>

        {/* Patient info cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {[
            { icon: User, label: "Patient", value: pi.name || "Not provided" },
            { icon: Calendar, label: "Date of Birth", value: pi.dob || "Not provided" },
            { icon: Calendar, label: "Test Date", value: pi.test_date || "Not provided" },
            { icon: Building2, label: "Laboratory", value: pi.lab || "Not provided" },
          ].map((item) => (
            <div key={item.label} className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="flex items-center gap-1.5 mb-2">
                <item.icon size={13} className="text-slate-400" />
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{item.label}</span>
              </div>
              <p className="text-sm font-medium text-slate-900">{item.value}</p>
            </div>
          ))}
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white border border-slate-200 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-slate-900">{report?.blood_markers?.length || 0}</p>
            <p className="text-xs text-slate-400 mt-0.5">Markers tested</p>
          </div>
          <div className={`border rounded-lg p-4 text-center ${abnormalCount > 0 ? "bg-red-50 border-red-200" : "bg-white border-slate-200"}`}>
            <p className={`text-2xl font-bold ${abnormalCount > 0 ? "text-red-700" : "text-slate-900"}`}>{abnormalCount}</p>
            <p className="text-xs text-slate-400 mt-0.5">Abnormal values</p>
          </div>
          <div className={`border rounded-lg p-4 text-center ${borderlineCount > 0 ? "bg-yellow-50 border-yellow-200" : "bg-white border-slate-200"}`}>
            <p className={`text-2xl font-bold ${borderlineCount > 0 ? "text-amber-700" : "text-slate-900"}`}>{borderlineCount}</p>
            <p className="text-xs text-slate-400 mt-0.5">Borderline values</p>
          </div>
        </div>

        {/* Overview */}
        <div data-testid={REPORT.overviewSection} className="bg-white border border-slate-200 rounded-lg p-6 mb-5">
          <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            <FileText size={18} className="text-blue-900" /> Overview
          </h2>
          <p className="text-slate-600 text-sm leading-relaxed">{report?.overview}</p>
        </div>

        {/* Blood Markers Table */}
        <div data-testid={REPORT.markersSection} className="bg-white border border-slate-200 rounded-lg overflow-hidden mb-5">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Blood Test Results</h2>
            <div className="flex items-center gap-3 text-xs">
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-emerald-400 inline-block" /> Normal</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-yellow-400 inline-block" /> Borderline</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-400 inline-block" /> Abnormal</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid={REPORT.markersTable}>
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Marker</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Value</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Unit</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Reference</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Explanation</th>
                </tr>
              </thead>
              <tbody>
                {(report?.blood_markers || []).map((m, i) => (
                  <MarkerRow key={m.name || i} marker={m} idx={i} />
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Key Findings */}
        {report?.key_findings?.length > 0 && (
          <div data-testid={REPORT.findingsSection} className="bg-white border border-slate-200 rounded-lg p-6 mb-5">
            <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>Key Findings</h2>
            <ul className="space-y-2">
              {report.key_findings.map((f, i) => (
                <li key={`finding-${i}`} className="flex items-start gap-2.5 text-sm text-slate-600">
                  <CheckCircle size={14} className="text-blue-900 mt-0.5 flex-shrink-0" /> {f}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Plain Language Summary */}
        <div data-testid={REPORT.summarySection} className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-5">
          <h2 className="text-lg font-semibold text-slate-900 mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>What This Means For You</h2>
          <p className="text-slate-700 text-sm leading-relaxed">{report?.plain_language_summary}</p>
        </div>

        {/* Recommendations */}
        {report?.recommendations?.length > 0 && (
          <div data-testid={REPORT.recommendationsSection} className="bg-white border border-slate-200 rounded-lg p-6 mb-5">
            <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>Medical Recommendations</h2>
            <ol className="space-y-3">
              {report.recommendations.map((r, i) => (
                <li key={`rec-${i}`} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-900 text-white text-xs font-bold flex items-center justify-center mt-0.5">
                    {i + 1}
                  </span>
                  <p className="text-sm text-slate-700 leading-relaxed">{r}</p>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Doctor's Notes */}
        <DoctorNotesSection reportId={reportId} initialNotes={report?.doctor_notes} />

        {/* Disclaimer */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-center">
          <p className="text-xs text-slate-400">
            This report is AI-generated for informational purposes only. Always consult a qualified healthcare professional for medical advice.
            Analysis powered by Gemini Flash · Generated on {formatDate(report?.created_at)}
          </p>
        </div>
      </main>
    </div>
  );
}
